"use client";

import { useMemo, useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { useWordList } from "@/components/tools/word-games/use-word-list";
import { WordListStatus } from "@/components/tools/word-games/word-list-status";
import { tPlural } from "@/lib/i18n/interpolate";
import {
  type BeePuzzle,
  type BeeResult,
  type BeeWord,
  buildHints,
  OUTER_COUNT,
  parsePuzzle,
  solveBee,
} from "@/lib/tool-kit/word-games/spelling-bee";

/** English strings. A translated content file overrides these through `ui`. */
export const SPELLING_BEE_SOLVER_STRINGS = {
  centerLabel: "Center letter",
  centerHint: "The letter every word must contain.",
  centerPlaceholder: "a",
  outerLabel: "The six outer letters",
  outerHint: "In any order. Spaces and commas are ignored.",
  outerPlaceholder: "c f t o r y",
  tryExample: "Try an example",
  hiveLabel: "Your hive",
  emptyPrompt:
    "Enter the center letter and the six outer letters to list every word the hive allows.",
  invalid: "Use the letters A–Z only.",
  centerLength: "Enter exactly one center letter.",
  outerCount: "Enter exactly six outer letters.",
  duplicate: "All seven letters must be different.",
  loading: "Loading the word list (once per visit)…",
  loadError:
    "Could not load the word list. Check your connection and try again.",
  retry: "Try again",
  heading: "Words in the hive",
  statWords: "Words",
  statPoints: "Points",
  statPangrams: "Pangrams",
  viewLabel: "Show",
  views: { words: "All words", hints: "Hints only (no spoilers)" },
  pangramsHeading: "Pangrams",
  pangramTag: "pangram",
  groupHeading: "one:{count} letter|other:{count} letters",
  groupCount: "one:{count} word|other:{count} words",
  copyAll: "Copy all",
  copied: "Copied!",
  noWords: "No words of four or more letters fit this hive.",
  hintsHeading: "Hint grid",
  hintsNote:
    "Counts of words by first letter and length. Words are hidden until you switch back.",
  hintLetter: "Starts with",
  hintTotal: "Total",
  pairsHeading: "Two-letter starts",
  rulesNote:
    "Words need four or more letters and the center letter; letters can repeat. Four-letter words score 1 point, longer words one point per letter, and a pangram (all seven letters) earns 7 more.",
  sourceNote:
    "Words come from ENABLE, a public-domain word-game list. It is not the list the puzzle uses, so you may see words the game rejects and miss some it accepts.",
} as const;

type Strings = typeof SPELLING_BEE_SOLVER_STRINGS;
type View = "words" | "hints";

const EXAMPLE = { center: "a", outer: "c f t o r y" };
const INVALID_KEYS = {
  invalid: "invalid",
  "center-length": "centerLength",
  "outer-count": "outerCount",
  duplicate: "duplicate",
} as const;

function Chip({ entry, strings }: { entry: BeeWord; strings: Strings }) {
  return (
    <li
      className={
        entry.pangram
          ? "rounded-md border border-primary bg-primary/10 px-2.5 py-1 font-bold font-mono text-foreground text-sm"
          : "rounded-md border border-border bg-card px-2.5 py-1 font-mono text-foreground text-sm"
      }
    >
      {entry.word}
      {entry.pangram ? (
        <>
          <span aria-hidden="true"> ★</span>
          <span className="sr-only"> {strings.pangramTag}</span>
        </>
      ) : null}
    </li>
  );
}

function Hive({ puzzle, label }: { puzzle: BeePuzzle; label: string }) {
  return (
    <div className="space-y-2">
      <p className={HINT}>{label}</p>
      <p className="flex flex-wrap gap-1.5">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-amber-400 bg-amber-300 font-bold text-amber-950 uppercase">
          {puzzle.center}
        </span>
        {puzzle.outer.map((letter) => (
          <span
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border bg-muted font-bold text-foreground uppercase"
            key={letter}
          >
            {letter}
          </span>
        ))}
      </p>
    </div>
  );
}

function WordGroups({
  result,
  strings,
}: {
  result: BeeResult;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const groups = useMemo(() => {
    const byLength = new Map<number, BeeWord[]>();
    for (const entry of result.words) {
      const group = byLength.get(entry.word.length) ?? [];
      group.push(entry);
      byLength.set(entry.word.length, group);
    }
    return [...byLength].sort((a, b) => a[0] - b[0]);
  }, [result]);
  const pangrams = result.words.filter((entry) => entry.pangram);
  return (
    <div className="space-y-5">
      {pangrams.length > 0 ? (
        <section className="space-y-2">
          <h3 className={WIDGET_H3}>{strings.pangramsHeading}</h3>
          <ul className="flex flex-wrap gap-2">
            {pangrams.map((entry) => (
              <Chip entry={entry} key={entry.word} strings={strings} />
            ))}
          </ul>
        </section>
      ) : null}
      {groups.map(([length, entries]) => (
        <section className="space-y-2" key={length}>
          <h3 className={WIDGET_H3}>
            {tPlural(strings.groupHeading, locale, length, {
              count: fmt(length),
            })}{" "}
            <span className={HINT}>
              {tPlural(strings.groupCount, locale, entries.length, {
                count: fmt(entries.length),
              })}
            </span>
          </h3>
          <ul className="flex flex-wrap gap-2">
            {entries.map((entry) => (
              <Chip entry={entry} key={entry.word} strings={strings} />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function Hints({ result, strings }: { result: BeeResult; strings: Strings }) {
  const fmt = useToolNumber();
  const hints = useMemo(() => buildHints(result.words), [result]);
  return (
    <div className="space-y-4">
      <p className={HINT}>{strings.hintsNote}</p>
      <h3 className={WIDGET_H3}>{strings.hintsHeading}</h3>
      <DataTable
        head={[
          strings.hintLetter,
          ...hints.lengths.map((length) => fmt(length)),
          strings.hintTotal,
        ]}
        rows={hints.rows.map((row) => [
          row.letter.toUpperCase(),
          ...row.counts.map((count) => (count === 0 ? "–" : fmt(count))),
          fmt(row.total),
        ])}
      />
      <h3 className={WIDGET_H3}>{strings.pairsHeading}</h3>
      <p className="font-mono text-foreground text-sm">
        {hints.pairs
          .map((pair) => `${pair.prefix.toUpperCase()}-${fmt(pair.count)}`)
          .join("  ")}
      </p>
    </div>
  );
}

function Results({
  result,
  view,
  strings,
}: {
  result: BeeResult;
  view: View;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  if (result.words.length === 0) {
    return <Notice>{strings.noWords}</Notice>;
  }
  return (
    <div aria-live="polite" className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h2 className={WIDGET_H2}>{strings.heading}</h2>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyAll}
          small
          text={result.words.map((entry) => entry.word).join("\n")}
        />
      </div>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.statWords}
          value={fmt(result.words.length)}
        />
        <StatCard label={strings.statPoints} value={fmt(result.points)} />
        <StatCard label={strings.statPangrams} value={fmt(result.pangrams)} />
      </StatGrid>
      {view === "words" ? (
        <WordGroups result={result} strings={strings} />
      ) : (
        <Hints result={result} strings={strings} />
      )}
    </div>
  );
}

export default function SpellingBeeSolverWidget() {
  const strings = useToolUi(SPELLING_BEE_SOLVER_STRINGS);
  const [center, setCenter] = useState("");
  const [outer, setOuter] = useState("");
  const [view, setView] = useState<View>("words");
  const parsed = parsePuzzle(center, outer);
  const { words, status, retry } = useWordList(parsed.ok);
  const result = useMemo(() => {
    const puzzle = parsePuzzle(center, outer);
    return puzzle.ok && words ? solveBee(words, puzzle.puzzle) : null;
  }, [center, outer, words]);

  function notice() {
    if (parsed.ok) {
      return null;
    }
    if (parsed.reason === "empty") {
      return <Notice>{strings.emptyPrompt}</Notice>;
    }
    const message = strings[INVALID_KEYS[parsed.reason]];
    // A half-typed hive is guidance, not an error: only bad characters and repeats alarm.
    return parsed.reason === "invalid" || parsed.reason === "duplicate" ? (
      <ErrorNotice>{message}</ErrorNotice>
    ) : (
      <Notice>{message}</Notice>
    );
  }

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-[1fr_2fr]">
        <TextField
          hint={strings.centerHint}
          id="bee-center"
          label={strings.centerLabel}
          mono
          onChange={(value) => setCenter(value.slice(0, 3))}
          placeholder={strings.centerPlaceholder}
          value={center}
        />
        <TextField
          hint={strings.outerHint}
          id="bee-outer"
          label={strings.outerLabel}
          mono
          onChange={(value) => setOuter(value.slice(0, OUTER_COUNT * 3))}
          placeholder={strings.outerPlaceholder}
          value={outer}
        />
      </div>
      <button
        className={BUTTON_SECONDARY}
        onClick={() => {
          setCenter(EXAMPLE.center);
          setOuter(EXAMPLE.outer);
        }}
        type="button"
      >
        {strings.tryExample}
      </button>
      {parsed.ok ? (
        <Hive label={strings.hiveLabel} puzzle={parsed.puzzle} />
      ) : null}
      {notice()}
      <WordListStatus
        error={strings.loadError}
        loading={strings.loading}
        onRetry={retry}
        retry={strings.retry}
        status={status}
      />
      {parsed.ok && result ? (
        <>
          <PillGroup
            label={strings.viewLabel}
            name="bee-view"
            onChange={setView}
            options={[
              { value: "words", label: strings.views.words },
              { value: "hints", label: strings.views.hints },
            ]}
            value={view}
          />
          <Results result={result} strings={strings} view={view} />
        </>
      ) : null}
      <p className={HINT}>{strings.rulesNote}</p>
      <p className={HINT}>{strings.sourceNote}</p>
    </>
  );
}
